import { useEffect, useState } from "react"
import { Link, Navigate } from "react-router"

import { Spinner } from "../../components/Spinner"
import HorizontalImageNotification from "../../components/HorizontalImageNotification"
import WizardFooter from "../../components/WizardFooter"
import GovtWizardYourPlanSummaryCard from "./GovtWizardYourPlanSummaryCard"
import GovtWizardNextStepsCard from "./GovtWizardNextStepsCard"
import GovtWizardSummaryStacksCard from "./GovtWizardSummaryStacksCard"
import {
  useAccountId,
  useAccountUrlPrefix,
  useGetGovtReport,
} from "../../hooks"
import { useGovernmentStore } from "../../stores/useGovernmentStore"
import CLIPBOARD from "../../images/clipboard.svg"
import AIRPLANE from "../../images/paper-airplane.svg"
import { ProjectListTypes } from "@/types/program"
import { GovtApWizardType } from "@/types/govtAppWizard"
import { AccountTypes } from "@/types/account"

export interface GovtWizardContentPlanSummaryProps {
  projectList: ProjectListTypes | undefined
  account: AccountTypes | undefined
  govtAppWizard: GovtApWizardType | undefined
}

const GovtWizardContentPlanSummary = ({
  projectList,
  account,
  govtAppWizard,
}: GovtWizardContentPlanSummaryProps) => {
  const accountId = useAccountId()
  const accountUrlPrefix = useAccountUrlPrefix()

  const { bookConsult } = useGovernmentStore()

  const [PDFDownloadLink, setPDFDownloadLink] = useState<string | null>(null)

  const {
    data: govtReport,
    isSuccess: isGovtReportSuccess,
    isLoading: isGovtReportLoading,
  } = useGetGovtReport<BlobPart, Error>(accountId, govtAppWizard!, {
    enabled: !!govtAppWizard,
  })

  useEffect(() => {
    if (isGovtReportSuccess && govtReport) {
      const blob = new Blob([govtReport], {
        type: "application/pdf",
      })
      const url = window.URL.createObjectURL(blob)
      setPDFDownloadLink(url)

      return () => {
        window.URL.revokeObjectURL(url)
      }
    }
  }, [isGovtReportSuccess, govtReport])

  if (isGovtReportLoading) {
    return (
      <div className="absolute inset-0 top-[66px] flex items-center justify-center pointer-events-none">
        <Spinner />
      </div>
    )
  }

  if (account?.is_member === false) {
    return (
      <Navigate replace to={`${accountUrlPrefix}/checkup/government/paywall`} />
    )
  }

  const isLegalEntity = Boolean(account?.legal_entity_name?.length)

  const isUnsureOrNone =
    govtAppWizard?.account_attestations.find((a) => a.attestation_id === "95")
      ?.value === "Unsure or none of the above"

  return (
    <div
      className="pt-10 lg:pt-13 pb-[202px] sm:pb-31"
      data-testid="govt-wizard-content-planSummary"
    >
      <div className="container w-[780px] max-w-full flex flex-col gap-6 px-5 lg:px-10">
        {bookConsult ? (
          <HorizontalImageNotification
            image={AIRPLANE}
            heading="Your meeting is in the works!"
            alt="airplane"
          >
            <p className="text-charcoal-500 text-base font-body leading-[140%] tracking-[0.32px] mt-2">
              One of our representatives will be reaching out to you in the next
              5 days to discuss your plan and implementation options.
            </p>

            <p className="text-charcoal-500 text-base font-body leading-[140%] tracking-[0.32px] mt-2">
              While you're waiting, you can review our NCX's{" "}
              <a
                href="https://help.ncx.com/hc/en-us/articles/22771858287899-How-to-apply-for-government-programs-like-EQIP-CSP-and-CRP"
                className="link"
                target="_blank"
                rel="noopener noreferrer"
              >
                How to apply to government programs
              </a>
            </p>
          </HorizontalImageNotification>
        ) : null}

        <GovtWizardYourPlanSummaryCard />

        <HorizontalImageNotification
          image={CLIPBOARD}
          heading="Print your document"
          alt="clipboard"
        >
          <p className="text-charcoal-500 text-base font-body leading-[140%] tracking-[0.32px] mt-2">
            We have created an easy to read and print document to guide the
            conversation with the NRCS representative. It will have all the
            answers to essential questions as well as your goals and potential
            practices you’ve selected and why.
          </p>

          {PDFDownloadLink ? (
            <a
              href={PDFDownloadLink || ""}
              download="plan-meeting-overview.pdf"
              className="govt-app-print-ncx-my-plan-meeting-overview-document-link inline-block link leading-[140%] tracking-[0.32px] mt-3"
            >
              Print NCX My Plan meeting overview document
            </a>
          ) : null}
        </HorizontalImageNotification>

        <GovtWizardNextStepsCard
          govtAppWizard={govtAppWizard}
          isLegalEntity={isLegalEntity}
          isUnsureOrNone={isUnsureOrNone}
        />

        <GovtWizardSummaryStacksCard
          projectList={projectList}
          govtAppWizard={govtAppWizard}
        />
      </div>

      <WizardFooter scrollToBottom={false}>
        <div className="flex gap-4 ml-auto">
          <Link
            to={`${accountUrlPrefix}/programs/page/1?category=saved_programs`}
            className="govt-app-page-5-go-to-saved-programs-button btn2 btn2-primary font-bold tracking-[0.32px] whitespace-nowrap"
          >
            Go to Saved Programs
          </Link>
        </div>
      </WizardFooter>
    </div>
  )
}

export default GovtWizardContentPlanSummary
